import * as React from "react";
import * as ReactDOM from "react-dom";
import { Style } from "source/app/presentation/Shared/Styles/CSS";
import { IBookOfMindState } from "../Helpers/IBookOfMindState";
import { BookOfMindSpeechController } from "../Speech/BookOfMindSpeechController";
import { SpeakQuotesLink } from "../Tools/Speak/SpeakQuotesLink";
import {
    BookOfMindOptionsButton,
    BookOfMindOptionsPanel,
} from "./Options/BookOfMindOptionsPanel";
import * as styles from "./Options/BookOfMindOptionsPanel.css";

const defaultFooterHeight = "56px";

export const NumberOfVerses = ({
    numVerses,
    numberOfQuotesToDisplay,
    totalQuotesToDisplay,
    rootNodeName,
    state,
}: {
    numVerses: number;
    numberOfQuotesToDisplay: number;
    totalQuotesToDisplay: number;
    rootNodeName: string;
    state: IBookOfMindState;
}) => {
    const shouldRender = numVerses > 0 && totalQuotesToDisplay > 0;
    const [footerTarget, setFooterTarget] = React.useState<HTMLElement | null>(
        null
    );
    const [isOptionsOpen, setIsOptionsOpen] = React.useState(false);
    const isSpeechActiveOrPaused =
        state.isSpeechActive || state.spokenQuoteIndex !== undefined;
    const showStandaloneSpeechRow = isSpeechActiveOrPaused;
    const footerHeight = showStandaloneSpeechRow ? "92px" : defaultFooterHeight;

    React.useLayoutEffect(() => {
        if (shouldRender) {
            setFooterTarget(document.getElementById("swalButtons-target"));
        }
    }, [shouldRender]);

    React.useLayoutEffect(() => {
        if (!footerTarget || footerHeight === defaultFooterHeight) {
            return;
        }

        footerTarget.style.height = footerHeight;

        const parentContainer = document.getElementById("parentContainer");
        if (parentContainer) {
            parentContainer.style.height = `calc(65vh - ${footerHeight})`;
        }

        return () => {
            footerTarget.style.removeProperty("height");
            parentContainer?.style.removeProperty("height");
        };
    }, [footerHeight, footerTarget]);

    if (!shouldRender) {
        return <BookOfMindSpeechController state={state} />;
    }

    return (
        <>
            <BookOfMindSpeechController state={state} />
            {footerTarget
                ? ReactDOM.createPortal(
                      <>
                          {isOptionsOpen ? (
                              <BookOfMindOptionsPanel
                                  rootNodeName={rootNodeName}
                                  state={state}
                              />
                          ) : null}
                          {showStandaloneSpeechRow ? (
                              <BookOfMindSpeechFooter state={state} />
                          ) : null}
                          <div className={styles.footerActions}>
                              <BookOfMindOptionsButton
                                  isOpen={isOptionsOpen}
                                  onClick={() => setIsOptionsOpen(!isOptionsOpen)}
                              />
                              {!showStandaloneSpeechRow ? (
                                  <span className={styles.footerSpeech}>
                                      <SpeakQuotesLink state={state} />
                                  </span>
                              ) : null}
                          </div>
                          <BookOfMindRange
                              {...{
                                  numVerses,
                                  numberOfQuotesToDisplay,
                                  totalQuotesToDisplay,
                              }}
                          />
                      </>,
                      footerTarget
                  )
                : null}
        </>
    );
};

function BookOfMindSpeechFooter({ state }: { state: IBookOfMindState }) {
    return (
        <span
            {...Style(`bookOfMindSpeechFooter`, {
                position: "absolute",
                right: 0,
                bottom: "44px",
                left: 0,
                display: "flex",
                justifyContent: "center",
                background: `linear-gradient(
                    to right,
                    transparent 0%,
                    rgba(72, 53, 25, 0.12) 16%,
                    rgba(72, 53, 25, 0.32) 50%,
                    rgba(72, 53, 25, 0.12) 84%,
                    transparent 100%
                )`,
                pointerEvents: "none",
                "& > span": {
                    display: "block",
                    width: "100%",
                    pointerEvents: "auto",
                },
            })}
        >
            <SpeakQuotesLink state={state} />
        </span>
    );
}

function BookOfMindRange({
    numVerses,
    numberOfQuotesToDisplay,
    totalQuotesToDisplay,
}: {
    numVerses: number;
    numberOfQuotesToDisplay: number;
    totalQuotesToDisplay: number;
}) {
    return (
        <span
            {...Style(`bookOfMindRange_footer`, {
                position: "absolute",
                right: "20px",
                bottom: "10px",
                color: "rgba(235, 210, 151, 0.8)",
                opacity: 0.62,
                fontSize: "12px",
                whiteSpace: "nowrap",
                pointerEvents: "none",
            })}
        >
            {totalQuotesToDisplay - numVerses + 1}
            <span {...Style(`bookOfMindRangeWords`, { fontSize: "10px", opacity: 0.6, margin: "0 4px" })}>
                to
            </span>
            {Math.min(
                totalQuotesToDisplay - numVerses + numberOfQuotesToDisplay,
                totalQuotesToDisplay
            )}
            <span {...Style(`bookOfMindRangeWords`, { fontSize: "10px", opacity: 0.6, margin: "0 4px" })}>
                of
            </span>
            {totalQuotesToDisplay}
        </span>
    );
}
